import { useMemo, useState } from "react";
import { Mail, MessageCircle, ExternalLink } from "lucide-react";
import Modal from "@/components/Modal";
import { useAuth } from "@/lib/auth";
import { stageMeta } from "@/lib/api";
import {
  buildEmailDraft,
  buildWhatsappDraft,
  gmailComposeUrl,
  whatsappUrl,
} from "@/lib/outreach";
import { toast } from "sonner";

/**
 * OutreachModal — compose a stage-aware Email or WhatsApp draft for a deal and
 * open it in Gmail / WhatsApp. Purely client-side: no provider credentials.
 *
 * channel: "email" | "whatsapp"
 * deal:    the deal (carries stage, title, fob_value, cif_value, exworks_value, customer_name)
 * customer: the customer (carries emails[], phones[], contact) — may be a stub
 */
export default function OutreachModal({ channel, deal, customer, onClose }) {
  const { user } = useAuth();
  const isEmail = channel === "email";

  const draft = useMemo(
    () => (isEmail ? buildEmailDraft(deal, customer, user) : buildWhatsappDraft(deal, customer, user)),
    [isEmail, deal, customer, user]
  );

  const [to, setTo] = useState(draft.to || "");
  const [subject, setSubject] = useState(draft.subject || "");
  const [body, setBody] = useState(draft.body || "");

  const emailOptions = customer?.emails || [];
  const phoneOptions = customer?.phones || [];
  // Email supports several recipients at once (comma-separated in the "To"
  // field); WhatsApp (wa.me) can only target a single number.
  const selectedEmails = to.split(",").map((s) => s.trim()).filter(Boolean);
  const toggleEmail = (v) => {
    const next = selectedEmails.includes(v)
      ? selectedEmails.filter((e) => e !== v)
      : [...selectedEmails, v];
    setTo(next.join(", "));
  };

  const open = () => {
    if (!to.trim()) {
      toast.error(isEmail ? "Recipient email is required" : "Recipient phone is required");
      return;
    }
    const url = isEmail
      ? gmailComposeUrl({ to: to.trim(), subject, body })
      : whatsappUrl({ to: to.trim(), body });
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success(isEmail ? "Opening Gmail…" : "Opening WhatsApp…");
    onClose();
  };

  const m = stageMeta(deal?.stage);
  const Icon = isEmail ? Mail : MessageCircle;
  const title = isEmail ? "Send Email" : "Send WhatsApp";
  const sub = `${deal?.customer_name || customer?.company || ""} · ${m.label} stage`;

  return (
    <Modal title={title} subtitle={sub} onClose={onClose} size="lg">
      <div className="grid gap-4" data-testid={`outreach-${channel}-modal`}>
        <div style={{ fontSize: 12, color: "var(--text-2)" }}>
          Draft is pre-filled from the lead's <strong>{m.label}</strong> stage. Edit anything below, then
          {isEmail ? " open it in Gmail." : " open it in WhatsApp."}
        </div>

        {/* Recipient */}
        <div>
          <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>
            {isEmail ? "To (email)" : "To (phone, with country code)"}
          </label>
          <input
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="input-base"
            placeholder={isEmail ? "name@company.com, other@company.com" : "+91 98201 34567"}
            data-testid={`outreach-${channel}-to`}
          />
          {(isEmail ? emailOptions : phoneOptions).length > 1 && (
            <div className="flex flex-wrap gap-1 mt-2 items-center">
              {isEmail && (
                <span style={{ fontSize: 10, color: "var(--text-2)", marginRight: 2 }}>Tap to add / remove recipients:</span>
              )}
              {(isEmail ? emailOptions : phoneOptions).map((v) => {
                const active = isEmail ? selectedEmails.includes(v) : to.trim() === v;
                return (
                  <button
                    key={v}
                    type="button"
                    onClick={() => (isEmail ? toggleEmail(v) : setTo(v))}
                    className="tag"
                    style={{
                      fontSize: 10,
                      cursor: "pointer",
                      background: active ? "var(--primary)" : "var(--surface-2)",
                      borderColor: active ? "var(--primary)" : "var(--border)",
                      color: active ? "#fff" : "var(--text-2)",
                    }}
                  >
                    {v}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {isEmail && (
          <div>
            <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>Subject</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} className="input-base" data-testid="outreach-email-subject" />
          </div>
        )}

        <div>
          <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>Message</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={isEmail ? 12 : 10}
            className="input-base"
            style={{ resize: "vertical", fontFamily: "inherit", whiteSpace: "pre-wrap" }}
            data-testid={`outreach-${channel}-body`}
          />
        </div>

        <div className="flex gap-3 justify-end mt-2">
          <button onClick={onClose} className="btn-outline" data-testid={`outreach-${channel}-cancel`}>Cancel</button>
          <button onClick={open} className="btn-primary" style={{ display: "flex", alignItems: "center", gap: 6 }} data-testid={`outreach-${channel}-open`}>
            <Icon size={14} />
            {isEmail ? "Open in Gmail" : "Open in WhatsApp"}
            <ExternalLink size={13} />
          </button>
        </div>
      </div>
    </Modal>
  );
}
